<template>
  <content-dialog
    :visible.sync="modalVisible"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    :small-title="$vuetify.breakpoint.xsOnly"
    persistent
    max-width="700px"
  >
    <!--<editor-fold desc="Dialog Title">-->
    <template slot="dialogTitle">
      <span v-if="serviceTaskDirty.id">
        Edit Service Task
      </span>
      <span v-else>
        Add New Service Task
      </span>
    </template>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Dialog Content">-->
    <v-card-text :class="{'pa-6': $vuetify.breakpoint.smAndUp }">
      <validation-observer ref="validationObserver">
        <!--<editor-fold desc="Basic info">-->
        <validation-provider
          v-slot="{ errors }"
          rules="required"
          name="yacht"
        >
          <yacht-modal-select
            v-model="serviceTaskDirty.yacht"
            :dialog-visible.sync="yachtModalSelectVisible"
            :error-messages="errors"
            outlined
            clearable
            :force-dialog="$vuetify.breakpoint.xsOnly"
            label="Yacht"
            @submit="yachtModalSelectVisible = false"
          />
        </validation-provider>
        <validation-provider
          v-slot="{ errors }"
          rules="required"
          name="task"
        >
          <repository-task-modal-select
            v-model="serviceTaskDirty.service_tasks_repository"
            :dialog-visible.sync="serviceTaskRepositoryModalSelectVisible"
            :error-messages="errors"
            outlined
            clearable
            :force-dialog="$vuetify.breakpoint.xsOnly"
            label="Task"
            @submit="serviceTaskRepositoryModalSelectVisible = false"
          />
        </validation-provider>

        <div v-if="oldTask && !serviceTaskDirty.id">
          <v-alert
            type="info"
            color="primary"
            outlined
            elevation="10"
            class="mb-8"
          >
            <div class="mb-2">
              Task is already opened by <strong>{{ oldTask.created_by.name }}</strong> on {{ oldTask.created_at | moment('D MMM YYYY HH:mm:ss') }}
            </div>
            <div
              class="mb-6 grey--text text--darken-2 text-body-2"
            >
              <strong>Description:</strong> {{ oldTask.description || 'No description' }}
            </div>
            <div>
              <v-btn
                color="primary"
                @click="$emit('serviceTask:view', oldTask); modalVisible = false"
              >
                View Task
              </v-btn>
              <v-btn
                class="ml-2"
                outlined
                color="primary"
                @click="$emit('serviceTask:logTime', oldTask)"
              >
                Add Work Log
              </v-btn>
              <v-btn
                class="ml-2"
                outlined
                color="primary"
                @click="$emit('serviceTask:finish', oldTask)"
              >
                Finish Task
              </v-btn>
            </div>
          </v-alert>
        </div>

        <v-textarea
          v-model="serviceTaskDirty.description"
          outlined
          label="Description"
        />
        <!--</editor-fold>-->

        <v-divider class="mb-6" />

        <div v-if="!serviceTaskDirty.id">
          <!--<editor-fold desc="Task status (finished/unfinished) toggle">-->
          <v-subheader>
            Task Status
          </v-subheader>

          <v-btn-toggle
            v-model="taskStatusBtn"
            class="v-btn-toggle--block elevation-1 mb-8 row no-gutters"
            color="white"
            active-class="primary"
            borderless
            mandatory
          >
            <v-btn
              large
              value="finished"
              class="flex-grow-1"
            >
              <v-icon
                left
                :color="isFinishedStatus ? 'white' : null"
              >
                check
              </v-icon>
              Finished
            </v-btn>
            <v-btn
              large
              value="not-finished"
              class="flex-grow-1"
            >
              <v-icon
                left
                :color="isNotFinishedStatus ? 'white' : null"
              >
                close
              </v-icon>
              Not Finished
            </v-btn>
            <v-btn
              large
              value="time-log"
              class="flex-grow-1"
            >
              <v-icon
                left
                :color="isTimeLogStatus ? 'white' : null"
              >
                query_builder
              </v-icon>
              Work Log
            </v-btn>
          </v-btn-toggle>
          <!--</editor-fold>-->

          <!--<editor-fold desc="Inputs visible only if task finished">-->
          <div v-if="isFinishedStatus || isTimeLogStatus">
            <validation-provider
              v-slot="{ errors }"
              rules="required"
              name=" "
            >
              <assignee-modal-select
                v-model="serviceTaskDirty.assigned_to"
                :error-messages="errors"
                multiple
                outlined
                clearable
                chips
                small-chips
                deletable-chips
                label="Work Done By"
                :dialog-visible.sync="assigneeModalSelectVisible"
                @submit="assigneeModalSelectVisible = false"
              />
            </validation-provider>

            <validation-provider
              v-slot="{ errors }"
              rules="required|min_value:0.1"
              name="hours spent"
            >
              <v-text-field
                v-model="serviceTaskDirty.hours_spent"
                name="hours_spent"
                :error-messages="errors"
                type="number"
                step="0.5"
                min="0.1"
                label="Hours Spent"
                outlined
                style="max-width: 150px"
              />
            </validation-provider>
          </div>
          <!--</editor-fold>-->
        </div>

        <div v-if="isFinishedStatus && $can('edit-finished-date', 'ServiceTask')">
          <v-subheader>
            Task Finished
          </v-subheader>

          <v-menu>
            <template v-slot:activator="{ on }">
              <validation-provider
                v-slot="{ errors }"
                rules="required"
                name="date finished"
              >
                <v-text-field
                  outlined
                  :value="serviceTaskDirty.finished_at ? $moment(serviceTaskDirty.finished_at).format('DD.MM.YYYY.') : ''"
                  style="max-width: 150px"
                  :error-messages="errors"
                  readonly
                  v-on="on"
                />
              </validation-provider>
            </template>
            <v-date-picker v-model="serviceTaskDirty.finished_at" />
          </v-menu>
        </div>

        <!--<editor-fold desc="Inventory items used">-->
        <v-row
          v-if="serviceTaskDirty.id || !isTimeLogStatus"
          no-gutters
          align="center"
        >
          <v-col>
            <v-subheader>
              Parts used
            </v-subheader>
          </v-col>
          <v-col
            v-if="canAddCustomParts"
            cols="auto"
          >
            <v-btn
              color="secondary"
              text
              @click="$refs.inventoryItemModalSelect.addNewCustomPart()"
            >
              <v-icon left>
                add
              </v-icon>
              Add Custom Parts
            </v-btn>
          </v-col>
          <v-col
            cols="auto"
          >
            <v-btn
              color="secondary"
              text
              @click="inventoryItemsModalSelectVisible = true"
            >
              <v-icon left>
                add
              </v-icon>
              Add Parts
            </v-btn>
          </v-col>
        </v-row>

        <inventory-item-modal-select
          ref="inventoryItemModalSelect"
          v-model="serviceTaskDirty.inventory_items"
          :current-employee-id="currentEmployee.id"
          :dialog-visible.sync="inventoryItemsModalSelectVisible"
          :custom-part-dialog-visible.sync="customPartDialogVisible"
          :can-add-free-parts="canAddCustomParts"
        />
        <!--</editor-fold>-->

        <v-subheader class="mt-6">
          Priority
        </v-subheader>

        <v-btn-toggle
          v-model="serviceTaskDirty.priority"
          class="v-btn-toggle--block elevation-1 mb-8 row no-gutters"
          color="white"
          active-class="primary"
          borderless
          mandatory
        >
          <v-btn
            large
            :value="serviceTaskClass.PRIORITY_NORMAL"
            class="flex-grow-1"
          >
            <v-avatar
              size="14px"
              :color="serviceTaskClass.priorityData[serviceTaskClass.PRIORITY_NORMAL].color"
              class="mr-3"
            />
            {{ serviceTaskClass.priorityData[serviceTaskClass.PRIORITY_NORMAL].label }}
          </v-btn>
          <v-btn
            large
            :value="serviceTaskClass.PRIORITY_HIGH"
            class="flex-grow-1"
          >
            <v-avatar
              size="14px"
              :color="serviceTaskClass.priorityData[serviceTaskClass.PRIORITY_HIGH].color"
              class="mr-3"
            />
            {{ serviceTaskClass.priorityData[serviceTaskClass.PRIORITY_HIGH].label }}
          </v-btn>
          <v-btn
            large
            :value="serviceTaskClass.PRIORITY_CRITICAL"
            class="flex-grow-1"
          >
            <v-avatar
              size="14px"
              :color="serviceTaskClass.priorityData[serviceTaskClass.PRIORITY_CRITICAL].color"
              class="mr-3"
            />
            {{ serviceTaskClass.priorityData[serviceTaskClass.PRIORITY_CRITICAL].label }}
          </v-btn>
        </v-btn-toggle>

        <!--<editor-fold desc="Insurance coverage">-->
        <v-subheader>
          Insurance Coverage
        </v-subheader>

        <v-row no-gutters>
          <v-col>
            <v-switch
              v-model="serviceTaskDirty.deposit_insurance"
              class="ma-0"
              label="Damage by charter client"
            />
          </v-col>
          <v-col>
            <v-switch
              v-model="serviceTaskDirty.hull_insurance"
              class="ma-0"
              label="Hull Insurance"
            />
          </v-col>
        </v-row>
        <!--</editor-fold>-->

        <v-subheader>
          Other
        </v-subheader>

        <v-row no-gutters>
          <v-col>
            <v-switch
              v-model="serviceTaskDirty.is_warranty_claim"
              class="ma-0"
              label="Warranty Claim"
            />
          </v-col>
          <v-col>
            <v-switch
              v-model="serviceTaskDirty.is_intervention"
              class="ma-0"
              label="Service Call"
            />
          </v-col>
        </v-row>

        <div v-if="serviceTaskDirty.warranty_insurance_note || serviceTaskDirty.is_warranty_claim || serviceTaskDirty.deposit_insurance || serviceTaskDirty.hull_insurance">
          <v-subheader>
            Warranty / Insurance Details
          </v-subheader>

          <v-textarea
            v-model="serviceTaskDirty.warranty_insurance_note"
            outlined
            placeholder="Warranty ticket ID / Insurance claim ID / other..."
            hint="Add details like warranty claim ticket or insurance claim ID"
            rows="3"
          />
        </div>

        <div v-if="serviceTaskDirty.is_warranty_claim">
          <v-subheader>
            Warranty Resolution
          </v-subheader>

          <v-radio-group
            v-model="serviceTaskDirty.warranty_claim_resolution"
            row
            :mandatory="false"
          >
            <v-radio
              :value="null"
              label="Pending"
            />
            <v-radio
              :value="true"
              label="Accepted"
              color="success"
            />
            <v-radio
              :value="false"
              label="Declined"
              color="error"
            />
          </v-radio-group>

          <div v-if="serviceTaskDirty.warranty_claim_resolution === true">
            <v-row
              no-gutters
              style="width: 350px"
            >
              <v-col cols="10">
                <v-text-field
                  v-model="serviceTaskDirty.warranty_claim_accepted_amount"
                  outlined
                  type="number"
                  label="Warranty Accepted Amount"
                  hint="Leave empty if unknown"
                />
              </v-col>
              <v-col class="pl-1">
                <v-select
                  v-model="serviceTaskDirty.warranty_claim_accepted_amount_currency"
                  label="Currency"
                  outlined
                  single-line
                  :items="currencies"
                />
              </v-col>
            </v-row>
          </div>
        </div>

        <!--<editor-fold desc="Images upload">-->
        <v-subheader class="mt-6">
          Upload Image
        </v-subheader>
        <dropzone
          :initial-files="serviceTaskDirty.files"
          :entity-type="fileTypeServiceTask"
          :category="fileCategoryPhoto"
        />
        <!--</editor-fold>-->
      </validation-observer>
    </v-card-text>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Dialog Actions">-->
    <template slot="dialogActions">
      <v-spacer />
      <v-btn
        text
        color="grey"
        @click="modalVisible = false"
      >
        Cancel
      </v-btn>
      <v-btn
        v-if="!serviceTaskDirty.id"
        color="primary"
        :loading="submitting"
        @click="submit(true)"
      >
        Save and Add New
      </v-btn>
      <v-btn
        color="primary"
        :loading="submitting"
        @click="submit()"
      >
        Save
      </v-btn>
    </template>
    <!--</editor-fold>-->
  </content-dialog>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep'
import { mapState } from 'vuex'
import validationScrollMixin from '@/mixins/validationScrollMixin'
import ContentDialog from '@/components/helpers/Dialog'
import ServiceTask from '@/models/ServiceTask'
import File from '@/models/File'
import Dropzone from '@/components/helpers/Dropzone'
import YachtModalSelect from '@/components/service/helpers/YachtModalSelect'
import RepositoryTaskModalSelect from '@/components/service/helpers/RepositoryTaskModalSelect'
import AssigneeModalSelect from '@/components/service/helpers/AssigneeModalSelect'
import InventoryItemModalSelect from '@/components/service/helpers/InventoryItemModalSelect'
import CurrencyExchangeRate from '@/models/CurrencyExchangeRate'
import serviceCurrentLocationMixin from '@/mixins/serviceCurrentLocationMixin'

export default {
    name: 'ServiceTaskEditDialog',
    components: {
      InventoryItemModalSelect,
      AssigneeModalSelect,
      RepositoryTaskModalSelect,
      YachtModalSelect,
      ContentDialog,
      Dropzone
    },
    mixins: [
      validationScrollMixin, serviceCurrentLocationMixin
    ],
    props: {
      visible: Boolean,
      serviceTask: {
        type: Object,
        default: () => ServiceTask.emptyModel
      }
    },
    data() {
      return {
        serviceTaskDirty: cloneDeep(this.serviceTask),
        inventoryItemsIssuedDirty: [],
        yachtModalSelectVisible: false,
        serviceTaskRepositoryModalSelectVisible: false,
        assigneeModalSelectVisible: false,
        inventoryItemsModalSelectVisible: false,
        customPartDialogVisible: false,
        dueAtDatepickerDialog: false,
        submitting: false,
        taskStatusBtn: this.serviceTask.finished ? 'finished' : 'not-finished',
        oldTask: undefined
      }
    },
    computed: {
      isFinishedStatus() {
        return this.taskStatusBtn === 'finished'
      },
      isNotFinishedStatus() {
        return this.taskStatusBtn === 'not-finished'
      },
      isTimeLogStatus() {
        return this.taskStatusBtn === 'time-log'
      },
      canAddCustomParts() {
        return true
      },
      modalVisible: {
        get: function () {
          return this.visible
        },
        set: function (value) {
          this.$emit('update:visible', value)
        }
      },
      fileCategoryPhoto() {
        return File.CATEGORY_PHOTO
      },
      fileTypeServiceTask() {
        return File.TYPE_SERVICE_TASK
      },
      currentEmployee() {
        return this.$store.state.auth.user.staff
      },
      ...mapState('service', ['inventoryItemsIssued'])
    },
    watch: {
      serviceTask: function () {
        this.prepareTask()
      },
      'serviceTaskDirty.deposit_insurance': function (newVal) {
         if (newVal) this.serviceTaskDirty.hull_insurance = false
      },
      'serviceTaskDirty.hull_insurance': function (newVal) {
        if (newVal) this.serviceTaskDirty.deposit_insurance = false
      },
      'serviceTaskDirty.service_tasks_repository': function (_serviceTaskRepository) {
        if (_serviceTaskRepository && _serviceTaskRepository.description && !this.serviceTaskDirty.description) {
         this.serviceTaskDirty.description =
           _serviceTaskRepository.description
                    .replace(/<[^>]*>?/gm, '')
                    .replace(/&nbsp;/gi, ' ')
                    .replace(/&amp;/gi, '&')
                    .replace(/&quot;/gi, '"')
                    .replace(/&lt;/gi, '<')
                    .replace(/&gt;/gi, '>')
        }

        this.fetchOpenTasks()
      },
      'serviceTaskDirty.yacht': function (_yacht) {
        this.fetchOpenTasks()
      }
    },
    created() {
      this.prepareTask()

      this.serviceTaskClass = ServiceTask
      this.currencies = CurrencyExchangeRate.currencies
    },
    methods: {
      prepareTask() {
        this.serviceTaskDirty = cloneDeep(this.serviceTask)
        this.prepareCurrentEmployeeInventory()

        if (this.serviceTaskDirty.assigned_to.length === 0) this.pushCurrentEmployee()

        this.prepareCustomParts()

        if (!this.serviceTaskDirty.id) {
          this.serviceTaskDirty.finished_at = this.$moment().format('YYYY-MM-DD')
        }
      },
      prepareCurrentEmployeeInventory() {
        let inventoryItemsIssuedDirty = cloneDeep(this.inventoryItemsIssued.data).map(i => ({
          ...i,
          amount_used: null,
          amount_max: i.amount
        }))
        // add data from current used inventory items
        this.serviceTaskDirty.inventory_items.map(usedItem => {
          if (usedItem.employee_id === this.currentEmployee.id) {
            // find in current inventory
            let inventoryItemIssued = inventoryItemsIssuedDirty.find(i => i.inventory_item_id === usedItem.id)
            // if item isn't present in inventory, create and push
            if (!inventoryItemIssued) {
              inventoryItemIssued = {
                amount: 0,
                inventory_item_id: usedItem.id,
                staff_id: this.currentEmployee.id,
                inventory_item: { ...usedItem },
                staff: { ...this.currentEmployee }
              }
              inventoryItemsIssuedDirty.push(inventoryItemIssued)
            }
            inventoryItemIssued.amount_used = usedItem.quantity
            inventoryItemIssued.amount_max = Number(inventoryItemIssued.amount_used) + Number(inventoryItemIssued.amount)
          }
        })
        this.inventoryItemsIssuedDirty = inventoryItemsIssuedDirty
      },
      pushCurrentEmployee() {
        let employee = cloneDeep(this.currentEmployee)
        employee.type = ServiceTask.ASSIGNEE_TYPE_STAFF

        this.serviceTaskDirty.assigned_to.push(employee)
      },
      prepareCustomParts() {
        for (let item of this.serviceTaskDirty.custom_parts) {
          this.serviceTaskDirty.inventory_items.push({ ...item, custom: true })
        }
      },
      async submit(addNew = false) {
        let validate = await this.$refs.validationObserver.validate()

        if (!validate) {
          this.showAlertAndScrollToFirstError()
          return
        }

        await this.saveServiceTask(addNew)
      },
      async saveServiceTask(addNew = false) {
        this.submitting = true

        let formData = {
          category: this.serviceTaskDirty.category,
          yacht_id: this.serviceTaskDirty.yacht.id,
          service_tasks_repository_id: this.serviceTaskDirty.service_tasks_repository.id,
          // method: this.serviceTaskDirty.method,
          finished: !this.serviceTaskDirty.id ? this.isFinishedStatus : undefined,
          hours_spent: (!this.isNotFinishedStatus && !this.serviceTaskDirty.id) ? this.serviceTaskDirty.hours_spent : undefined,
          deposit_insurance: this.serviceTaskDirty.deposit_insurance,
          hull_insurance: this.serviceTaskDirty.hull_insurance,
          is_intervention: this.serviceTaskDirty.is_intervention,
          is_warranty_claim: this.serviceTaskDirty.is_warranty_claim,
          warranty_insurance_note: this.serviceTaskDirty.warranty_insurance_note,
          warranty_claim_resolution: this.serviceTaskDirty.warranty_claim_resolution,
          warranty_claim_accepted_amount: this.serviceTaskDirty.warranty_claim_accepted_amount,
          warranty_claim_accepted_amount_currency: this.serviceTaskDirty.warranty_claim_accepted_amount_currency,
          is_time_log: this.isTimeLogStatus,
          assigned_to: (!this.isNotFinishedStatus && !this.serviceTaskDirty.id) ? this.serviceTaskDirty.assigned_to.map(assignee => {
            return {
              type: assignee.type,
              id: assignee.id
            }
          }) : undefined,
          description: this.serviceTaskDirty.description,
          priority: this.serviceTaskDirty.priority,
          files: {
            // todo: this code repeats in yachtModelEditDialog
            data: this.$store.state.dropzone.files.map(file => { return file.response }),
            removedFileIds: this.$store.state.dropzone.removedIds
          },
          inventory_items: this.serviceTaskDirty.inventory_items
            .map(i => ({
              id: i.id,
              quantity: i.quantity,
              unit: i.unit,
              unit_price: i.unit_price,
              currency: i.currency,
              label: i.label,
              code: i.code
            }))
        }

        if (this.$can('edit-finished-date', 'ServiceTask')) {
          formData.finished_at = this.serviceTaskDirty.finished_at
        }

        try {
          const savedServiceTask = await ServiceTask.api.save(formData, this.serviceTaskDirty.id)

          this.$store.dispatch('alert', {
            'text': 'Service Task Saved',
            'color': 'success'
          })

          this.$store.dispatch('clearFiles')
          this.$emit(this.serviceTaskDirty.id ? 'serviceTask:updated' : 'serviceTask:created', savedServiceTask)

          if (addNew) {
            const yacht = this.serviceTaskDirty.yacht

            this.prepareTask()

            this.serviceTaskDirty.yacht_id = yacht.id
            this.serviceTaskDirty.yacht = yacht

            this.$refs.validationObserver.reset()

            this.$vuetify.goTo(0, { container: '.v-dialog.v-dialog--active .v-card__text' })
          } else {
            this.modalVisible = false
          }
        } catch (e) {
          if (e.response && e.response.status === 422) {
            this.$store.dispatch('alert', {
              text: 'Invalid entry: ' + e.response.data.errors[Object.keys(e.response.data.errors)[0]][0],
              color: 'error'
            })
          } else {
            this.$store.dispatch('alert', {
              'text': 'Something went wrong',
              'color': 'error'
            })
          }

          throw e
        } finally {
          this.submitting = false
        }
      },
      async fetchOpenTasks() {
        if (!this.serviceTaskDirty.yacht || !this.serviceTaskDirty.service_tasks_repository) return

        try {
          const oldTasks = await ServiceTask.api.fetchList({
            service_tasks_repository_id: this.serviceTaskDirty.service_tasks_repository.id,
            yacht_id: this.serviceTaskDirty.yacht.id,
            finished: false,
            ...this.currentLocationQueryParams
          })

          if (oldTasks.length > 0) {
            this.oldTask = oldTasks[0]
          } else {
            this.oldTask = undefined
          }
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        }
      }
    }
  }
</script>
